/**
 * Shared warehouse-days derivation for the Banker Reconciliation.
 *
 * Days on the warehouse line are DERIVED from the funds dates (inclusive), not carried as an auto-filled
 * scalar. Both the Warehouse tab (display) and BankerRecon (save) consume `effectiveWarehouseDays` so the
 * shown value and the persisted `warehouse_days` always agree. Lives here (not in a tab component) so the
 * parent never imports from a child.
 */

import { parseAsLocalDate } from '@/utils/dateHelpers';
import type { KeyDates } from '@/components/banker-recon/types';

const parseDate = (dateStr: string | null | undefined): Date | null => {
  if (!dateStr) {
    return null;
  }
  const date = parseAsLocalDate(dateStr);
  return isNaN(date.getTime()) ? null : date;
};

/** Inclusive day count between two date strings (ceil of the diff, +1). 0 when either date is unparseable. */
export const calculateDaysBetween = (start: string, end: string): number => {
  const startDate = parseDate(start);
  const endDate = parseDate(end);
  if (!startDate || !endDate) {
    return 0;
  }
  const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
};

/**
 * Days on warehouse for display AND save. **Dates-win:** when both funds dates exist, use the inclusive
 * date diff; fall back to the stored scalar only when a date is missing — so a stale `warehouse_days > 0`
 * can never override the dates.
 */
export const effectiveWarehouseDays = (keyDates: KeyDates): number => {
  if (keyDates.fundsToEscrow && keyDates.fundsReceived) {
    return calculateDaysBetween(keyDates.fundsToEscrow, keyDates.fundsReceived);
  }
  return keyDates.warehouseDays > 0 ? keyDates.warehouseDays : 0;
};
